import { useNavigate, useLocation } from "react-router-dom";
import { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import useApi from "../hooks/useApi";

const registrationDataContext = createContext({});

export const RegistrationDataProvider = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { post, loading } = useApi();
  const { pathname, state } = useLocation();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [register_password, setRegister_password] = useState("");
  const [register_email, setRegister_email] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [address, setAddress] = useState("");
  const [activityName, setActivityName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [pricePlan, setPricePlan] = useState("");

  const [termsAggred, setTermsAggred] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const [registerValidation, setRegisterValidation] = useState("");
  const [successLoader, setSuccessLoader] = useState(false);

  const [emailNeedsVerification, setEmailNeedsVerification] = useState(true);

  const [providerRegistrationSteps, setProviderRegistrationSteps] = useState(0);

  useEffect(() => {
    if (state) {
      setRegister_email(state?.email || "");
      setFirstName(state?.name || "");
      setLastName(state?.surname || "");
    }
  }, [state]);

  // let upperCaseLetters = /[A-Z]/g;
  // let number = /[0-9]/g;
  // let specialChars = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  let emailValidation = /@./;

  const resetState = () => {
    setPhoneNumber("");
    setRegister_password("");
    setRegister_email("");
    setFirstName("");
    setLastName("");
    setCountry("");
    setCity("");
    setDistrict("");
    setAddress("");
    setActivityName("");
    setTermsAggred(false);
    setShowTermsError(false);
    setRegisterValidation("");
  };

  const FetchRegister = () => {
    return new Promise(async (resolve) => {
      if (
        firstName.trim() === "" ||
        lastName.trim() === "" ||
        register_email.trim() === "" ||
        register_password === "" ||
        phoneNumber.trim() === ""
      ) {
        setRegisterValidation(t("error_fill_all"));
        return;
      }

      if (isPossiblePhoneNumber("+" + phoneNumber) === false) {
        setRegisterValidation(t("error_incorrect_phone"));
        return;
      }

      if (!register_email.match(emailValidation)) {
        setRegisterValidation(t("error_incorrect_email"));
        return;
      }

      if (emailNeedsVerification) {
        setRegisterValidation(t("error_email_verification_needed"));
        return;
      }

      // if (register_password !== confirmPassword) {
      //   setRegisterValidation(t("error_password_confirm_failed"));
      //   return;
      // }

      // if (!register_password.match(upperCaseLetters)) {
      //   setRegisterValidation(t("error_password_contains_uppercase"));
      //   return;
      // }
      // if (!register_password.match(specialChars)) {
      //   setRegisterValidation(t("error_password_contains_special"));
      //   return;
      // }
      // if (!register_password.match(number)) {
      //   setRegisterValidation(t("error_password_contains_digit"));
      //   return;
      // }
      if (register_password.length < 8) {
        setRegisterValidation(t("error_password_length"));
        return;
      }

      if (!termsAggred) {
        setShowTermsError(true);
        return;
      }

      try {
        const userRole =
          pathname.includes("consumer-registration") ||
          pathname === "/role-selector"
            ? "Consumer"
            : "Provider";
        const navigateAfterSuccess =
          userRole === "Provider" ? "/schedules?firstTime=true" : "/sessions";

        const commonData = {
          phoneNumber,
          password: register_password,
          email: register_email,
          firstName,
          lastName,
          userRole,
        };

        const registerData = await post("/User", {
          ...commonData,
          ...(userRole === "Provider" && {
            country: country,
            city: city,
            district: district,
            address: address,
            activityName: activityName,
            activityCategory: selectedCategory,
            pricePlan: pricePlan ? pricePlan : "Starter",
          }),
        });

        if (!registerData.succeeded === true) {
          setRegisterValidation(registerData.errors[0].description);
          setSuccessLoader(false);
          return;
        }

        setSuccessLoader(true);

        const timeOut = setTimeout(() => {
          setSuccessLoader(false);

          clearTimeout(timeOut);
        }, [3000]);

        try {
          const loginData = await post("/User/login", {
            phoneNumber: phoneNumber,
            password: register_password,
            rememberLogin: true,
          });

          localStorage.setItem("ps10005000", JSON.stringify(loginData.token));
          localStorage.setItem(
            "ps10004000",
            JSON.stringify(loginData.refreshToken),
          );
          resetState();
          navigate(navigateAfterSuccess);
        } catch (error) {}
        setRegisterValidation(t("error_something_wrong"));
      } catch (error) {
        setRegisterValidation(t("error_something_wrong"));
      }
      resolve();
    });
  };

  const handleExternalRegister = async () => {
    if (!state.email) {
      if (
        firstName.trim() === "" ||
        lastName.trim() === "" ||
        register_email.trim() === "" ||
        phoneNumber.trim() === ""
      ) {
        setRegisterValidation(t("error_fill_all"));
        return;
      }

      if (isPossiblePhoneNumber("+" + phoneNumber) === false) {
        setRegisterValidation(t("error_incorrect_phone"));
        return;
      }

      if (!register_email.match(emailValidation)) {
        setRegisterValidation(t("error_incorrect_email"));
        return;
      }

      if (emailNeedsVerification) {
        setRegisterValidation(t("error_email_verification_needed"));
        return;
      }

      if (!termsAggred) {
        setShowTermsError(true);
        return;
      }
    }

    try {
      setRegisterValidation("");
      const userRole =
        pathname.includes("consumer-registration") ||
        pathname === "/role-selector"
          ? "Consumer"
          : "Provider";
      const navigateAfterSuccess =
        userRole === "Provider" ? "/schedules?firstTime=true" : "/sessions";

      const commonData = {
        email: register_email,
        firstName,
        lastName,
        provider: state?.provider,
        phoneNumber: phoneNumber || null,
        providerKey: state?.providerKey,
        accessToken: state?.accessToken,
        profileUrl: state?.profileUrl,
        userRole,
      };

      const registerData = await post("/User/external-register", {
        ...commonData,
        ...(userRole === "Provider" && {
          country: country,
          city: city,
          district: district,
          address: address,
          activityName: activityName,
          activityCategory: selectedCategory,
          pricePlan: state.pricePlan ? state.pricePlan : "Starter",
        }),
      });

      if (registerData.isAuthorized) {
        localStorage.setItem("ps10005000", JSON.stringify(registerData.token));
        localStorage.setItem(
          "ps10004000",
          JSON.stringify(registerData.refreshToken),
        );
        resetState();
        navigate(navigateAfterSuccess);
      }
    } catch (error) {
      setRegisterValidation(t("error_something_wrong"));
    }
  };

  return (
    <registrationDataContext.Provider
      value={{
        firstName,
        lastName,
        phoneNumber,
        register_password,
        register_email,
        registerValidation,
        country,
        city,
        district,
        address,
        activityName,
        pricePlan,
        providerRegistrationSteps,
        termsAggred,
        showTermsError,
        emailNeedsVerification,

        changeProviderRegistrationSteps: setProviderRegistrationSteps,
        changeFirstName: setFirstName,
        changeLastName: setLastName,
        changePhoneNumber: setPhoneNumber,
        changeRegister_password: setRegister_password,
        changeRegister_email: setRegister_email,
        changeRegisterValidation: setRegisterValidation,
        changeCountry: setCountry,
        changeCity: setCity,
        changeDistrict: setDistrict,
        changeAddress: setAddress,
        changeActivityName: setActivityName,
        selectCategory: setSelectedCategory,
        changePricePlan: setPricePlan,
        setTermsAggred,
        setShowTermsError,
        setEmailNeedsVerification,

        registrationLoader: loading,
        successLoader,
        FetchRegister,
        handleExternalRegister,
      }}
    >
      {children}
    </registrationDataContext.Provider>
  );
};

const useRegistration = () => useContext(registrationDataContext);

export default useRegistration;
